import { dropTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import Task, { TTaskType } from 'volta/models/task';

import { action } from '@ember/object';
import { later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';

import { TTaskResolveIf } from 'volta/models/types/tasks';

import type SessionUserService from 'volta/services/session-user';
import type IntlService from 'ember-intl/services/intl';

interface IBvTaskArgs {
  task: Task;
  isSelected?: boolean;
  disabled?: boolean;
  isResolvable?: boolean;
  isDue?: boolean;
  isAssignable?: boolean;
  taskType?: TTaskType;
  /* Actions */
  onSelect?: (task?: Task, forceDelete?: boolean) => void;
  onSubmit?: (task: Task) => void;
  onResolve?: (task: Task) => void;
  onDelete?: (task: Task) => void;
}

const ResolveIfIcons = {
  MANUAL: 'checkbox-outline',
  OUT_OF_BUFFER_ALERT: 'timer-outline'
};
export default class BvTask extends Component<IBvTaskArgs> {
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;

  ResolveIfIcons = ResolveIfIcons;

  get badgeColor() {
    const { taskType } = this.args.task;
    switch (taskType) {
      case 'EXECUTION_TASK':
        return 'green';
      case 'PLANNING_TASK':
        return 'blue';
      default:
        return 'primary';
    }
  }

  get isOwn() {
    return this.createdBy?.id === this.sessionUser.userId;
  }

  get createdBy() {
    return this.args.task.createdBy ?? this.sessionUser.user;
  }

  get createdAtDisplay() {
    const { createdAt } = this.args.task;
    return this.displayTimeAt(createdAt);
  }

  get resolvedAtDisplay() {
    const { resolvedAt } = this.args.task;
    return this.displayTimeAt(resolvedAt);
  }

  get formattedDueDate(): string | undefined {
    const dueDate = this.args.task.dueDateAsDate;

    if (dueDate) {
      const mDueDate = moment(dueDate);

      if (!mDueDate.isSame(moment(), 'year')) {
        return mDueDate.format('MM/YYYY');
      } else if (mDueDate.isSame(moment(), 'day')) {
        return this.intl.t('today');
      } else if (mDueDate.isSame(moment().add(1, 'day'), 'day')) {
        return this.intl.t('tomorrow');
      } else if (
        mDueDate.dayOfYear() > moment().dayOfYear() &&
        mDueDate.dayOfYear() < moment().dayOfYear() + 15
      ) {
        return mDueDate.fromNow(!this.args.isSelected);
      } else {
        // https://github.com/moment/moment/issues/3341
        return mDueDate
          .format('L')
          .replace(new RegExp('[^.]?' + moment().format('YYYY') + '.?'), '');
      }
    }
    return undefined;
  }

  handleBodyEnter = dropTask(async (_value: string, _id: string, event: KeyboardEvent) => {
    event.preventDefault();
    return await this.args.onSubmit?.(this.args.task);
  });

  @action
  changeResolution(resolveIf: TTaskResolveIf | undefined) {
    this.args.task.resolveIf = resolveIf;
    return this.args.onSubmit?.(this.args.task);
  }

  @action
  changeDueDate(dueDate: Date | undefined) {
    this.args.task.dueDate = dueDate ? moment(dueDate).format('YYYY-MM-DD') : undefined;
  }

  @action
  handleResolve(checked: boolean, e: InputEvent) {
    e.stopPropagation();

    later(
      () => {
        this.args.task.resolvedAt = this.args.task.resolvedAt ? null : new Date();
        this.args.onResolve?.(this.args.task);
      },
      checked ? 1000 : 500
    );
  }

  handleDelete = dropTask(async () => {
    return await this.args.onDelete?.(this.args.task);
  });

  @action
  handleClickOutside(forceDelete: boolean = false, _event: MouseEvent) {
    if (this.args.isSelected) {
      this.args.onSelect?.(undefined, forceDelete);
    }
  }

  private displayTimeAt(time?: Date | null): string | undefined {
    if (!time) {
      return undefined;
    }
    const mTime = moment(time);
    const isToday = mTime.isSame(moment(), 'day');
    const isYesterday = mTime.isSame(moment().subtract(1, 'day'));
    if (isToday || isYesterday) {
      return this.intl.t(isToday ? 'today' : 'yesterday');
    } else {
      return mTime.format('L');
    }
  }
}
